// Pre-seeded demo journey for YC video recording.
// Loaded via /sell?demo=1 — all AI stages pre-populated so every screen
// shows results instantly. Not saved to localStorage or Supabase.

const now = new Date().toISOString();

export const DEMO_ADDRESS = "2847 Broderick St, San Francisco, CA 94123";

export const DEMO_JOURNEY = {
  id: "demo-journey-yc2026",
  address: DEMO_ADDRESS,
  broker_id: null,
  currentStageIdx: 0,
  verification: {
    is_self: true,
    relationship: "self",
    owner_of_record: "Sarah M. Thornton",
    confirmed_at: now,
  },
  property: {
    property: [{
      building: {
        rooms: { beds: 3, bathstotal: 2, bathsfull: 2 },
        size: { bldgsize: 1650 },
        yearbuilt: 1922,
      },
      lot: { lotsize1: 2700 },
      summary: { proptype: "Single Family Residence" },
    }],
  },
  stages: {
    valuation: {
      status: "done",
      data: {
        text: `**Fair listing-price range: $1,850,000 – $2,050,000**

**Recommended list price:** $1,950,000

**Why this range:**
- 2791 Filbert St · 3bd/2ba · 1,580 sqft · $1,920,000 · $1,215/sqft · sold Apr 2026
- 3102 Broderick St · 3bd/2ba · 1,710 sqft · $2,015,000 · $1,178/sqft · sold Mar 2026
- 2654 Laguna St · 3bd/1ba · 1,490 sqft · $1,775,000 · $1,191/sqft · sold Feb 2026
- 2901 Pierce St · 3bd/2ba · 1,620 sqft · $1,865,000 · $1,151/sqft · sold Jan 2026

**Confidence:** High — four direct comps within 0.4 miles, all closed in the last 90 days, tight $/sqft spread of $1,151–$1,215.`,
        range: { low: 1850000, high: 2050000, midpoint: 1950000 },
        generated_at: now,
      },
    },

    prep: {
      status: "done",
      data: {
        roi_items: [
          { id: "refinish-floors", task: "Refinish original hardwood floors", cost_est: "$1,200–$1,800", value_lift: "~$20,000", priority: "high", why: "Original hardwood is a selling point; a dull finish undersells it in listing photos." },
          { id: "paint-exterior", task: "Fresh exterior paint — trim and front door", cost_est: "$800–$1,200", value_lift: "~$15,000", priority: "high", why: "1922 Edwardian buyers pay premiums for curb appeal; peeling trim is the #1 objection." },
          { id: "kitchen-hardware", task: "Replace cabinet hardware throughout kitchen", cost_est: "$200–$400", value_lift: "~$8,000", priority: "high", why: "Quick swap modernizes a dated kitchen without a remodel price tag." },
          { id: "bathroom-regrout", task: "Regrout master bath tile and recaulk tub surround", cost_est: "$300–$500", value_lift: "~$5,000", priority: "med", why: "Discolored grout signals deferred maintenance to buyers and inspectors." },
          { id: "staging", task: "Professional staging — living room and primary bedroom", cost_est: "$1,500–$2,500", value_lift: "~$25,000", priority: "med", why: "Staged Cow Hollow listings sell 12 days faster and 4.2% over ask on average." },
        ],
        generated_at: now,
      },
    },

    disclosures: {
      status: "done",
      data: {
        questions: [],
        answers: {},
        summary: `**Seller Disclosure Summary — 2847 Broderick St, San Francisco, CA 94123**

**Transfer Disclosure Statement (TDS):** No known material defects. Property is in good condition.

**Seller Property Questionnaire (SPQ):** Seismic retrofit completed 2019 (permit on file). Roof replaced 2017. 200-amp electrical upgrade 2018. No known water intrusion, mold, pest infestation, or foundation issues.

**Natural Hazard Disclosure (NHD):** Property is located in a Seismic Zone 4 area. Not in a flood zone. Not in a fire hazard severity zone.

**Lead Paint Disclosure:** Required — home was built in 1922, prior to 1978. Seller has no knowledge of existing lead paint hazards.

**HOA:** None.

Seller has owned the property since 2016 with no known disputes, litigation, or insurance claims.`,
        generated_at: now,
      },
    },

    staging: {
      status: "pending",
      data: null,
    },

    photography: {
      status: "pending",
      data: null,
    },

    listing: {
      status: "done",
      data: {
        headline: "Original Edwardian on Broderick — 3bd, updated kitchen, 3 blocks to the Marina Green",
        body: `Light pours through tall Victorian windows into a living room that still has its original built-in bookshelves and box-beam ceiling — the kind of detail that stopped being built in 1940. The kitchen was updated in 2021 with Caesarstone counters and a 36" Wolf range while keeping the butler's pantry intact. Three bedrooms upstairs, two full baths, original hardwood throughout.

Broderick at Cow Hollow puts you two blocks from the Saturday farmers market, three from the Marina Green, and a ten-minute walk to Chestnut Street. Top-rated schools, low traffic, and a neighborhood that hasn't gone out of style in a hundred years.

The foundation was seismically retrofitted in 2019, the roof replaced in 2017, and the electrical upgraded to 200-amp service. This is a move-in home, not a project — priced to sell at list.`,
        generated_at: now,
      },
    },

    mls: {
      status: "done",
      data: {
        submitted_at: now,
        portals: ["Regional MLS (broker-of-record)", "Zillow", "Redfin", "Trulia", "Realtor.com", "Homes.com"],
        verified_at: now,
      },
    },

    showings: {
      status: "in_progress",
      data: { scheduled: true },
    },

    openhouse: {
      status: "done",
      data: {
        mode: "diy",
        date: "2026-08-02",
        marketing: `**Instagram caption**
Original Edwardian in Cow Hollow — open this Saturday, 1–4 PM. 3bd, updated kitchen, seismically retrofitted. DM to RSVP. #SanFranciscoRealEstate #CowHollow #OpenHouse

**Email blast subject + body**
Subject: Open House Sat · 2847 Broderick St, SF

Join us Saturday, August 2nd, 1–4 PM at 2847 Broderick St.

Three bedrooms, updated kitchen, original hardwood throughout. Seismically retrofitted 2019. Offered at $1,950,000.

Private showings available same day — reply to book a slot.

**Door-hanger / postcard text**
Open House this Saturday, 1–4 PM. 2847 Broderick St — a beautifully maintained 1922 Edwardian in the heart of Cow Hollow. Three bedrooms, Caesarstone kitchen, refinished hardwood, and a foundation retrofit that gives you real peace of mind. Offered at $1,950,000.`,
        scheduled_at: now,
      },
    },

    offers: {
      status: "in_progress",
      data: {
        mode: "diy",
        offers: [
          { id: "A82", price: 1395000, close_days: 30, financing: "30% down conventional, 21d financing contingency", emd: 40000, inspection_contingency: "17 days", appraisal_contingency: "yes" },
          { id: "B11", price: 1420000, close_days: 21, financing: "All cash", emd: 50000, inspection_contingency: "10 days", appraisal_contingency: "waived" },
        ],
        analysis: `**Side-by-side**
- Offer A82 ($1,395,000 · 30d · conventional): Close probability 72% · Risk-weighted net $1,375,000 · Financing contingency is the primary risk
- Offer B11 ($1,420,000 · 21d · all cash): Close probability 94% · Risk-weighted net $1,413,000 · No financing or appraisal exposure

**Recommended strategy**
Accept Offer B11 as-is. All-cash at $1.42M closes 9 days faster and carries zero appraisal or financing blow-up risk. The $25,000 price premium combined with a 22-point close-probability advantage makes this straightforward.

**Counter terms for the leading offer**
- Shorten close to 17 days — all-cash should support it, and sellers gain interest on proceeds sooner
- Increase EMD to $70,000 to signal firm commitment
- Request proof of funds before counter is signed
- Sell as-is — no repairs or credits given the disclosed and documented condition`,
        generated_at: now,
      },
    },

    escrow: { status: "in_progress", data: null },
    closing: { status: "pending", data: null },
  },
};
